import { useMemo } from 'react';

import {
  Button,
  Container,
  Grid,
  LoadingOverlay,
  PasswordInput,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { isApiError } from '@api/index';
import {
  useCheckTokenQuery,
  usePasswordResetMutation,
} from '@api/passwordReset.api';

import { useTranslation } from 'react-i18next';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useGRecaptcha from '@hooks/useGRecaptcha';
import PasswordInputWithRequirements from './PasswordInputWithRequirements';

export default function PasswordReset() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [params, setParams] = useQueryParams();

  const { getRecaptchaToken } = useGRecaptcha();
  const { t } = useTranslation();

  const token = useMemo(() => params.get('t'), [params]);

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const form = useForm({
    initialValues,
    validate: {
      passwordConfirm: (value, values) =>
        value !== values.password
          ? t('auth.passwordReset.validation.passwordMismatch')
          : null,
    },
  });

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('PasswordReset');

      await passwordReset({
        token: token!,
        body: {
          password: values.password,
        },
        recaptchaToken,
      }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('auth.passwordReset.successMessage'));
      setParams(params);
    } catch (e) {
      if (isApiError(e)) {
        if (e.status === 400) {
          form.setErrors({ general: e.data.message, ...e.data.errors });
        } else if (e.status === 404) {
          // TODO
        }
      } else {
        form.setErrors({
          general: t('common.validation.unexpectedError'),
        });
      }
    }
  };

  // ==========================================================================
  // Api
  // ==========================================================================
  const { isError, isLoading: isLoadingCheckToken } = useCheckTokenQuery(
    token!,
  );

  const [passwordReset, { isLoading }] = usePasswordResetMutation();

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <LoadingOverlay visible={isLoadingCheckToken} />

      <Text ta="center" fw="bold" size="1.5rem">
        {t('auth.passwordReset.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('auth.passwordReset.knowPassword')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('auth.passwordReset.loginLink')}
        </Text>
      </Text>

      {isError ? (
        <>
          <Text mt="lg" ta="center">
            {t('auth.passwordReset.invalidToken')}
          </Text>
          <Text
            ta="center"
            c="brand"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              params.set('p', 'password-reset-request');
              setParams(params);
            }}
          >
            {t('auth.passwordReset.requestNew')}
          </Text>
        </>
      ) : (
        <form
          onSubmit={form.onSubmit((values) => {
            onSubmit(values);
          })}
        >
          <Text ta="center" mb="lg" mt="xl">
            {t('auth.passwordReset.enterNewPassword')}
          </Text>

          <Grid>
            <Grid.Col span={{ md: 6 }}>
              <PasswordInputWithRequirements
                label={t('auth.passwordReset.newPassword.label')}
                placeholder={t('auth.passwordReset.newPassword.placeholder')}
                required
                {...form.getInputProps('password')}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
              <PasswordInput
                label={t('auth.passwordReset.passwordConfirm.label')}
                placeholder={t(
                  'auth.passwordReset.passwordConfirm.placeholder',
                )}
                required
                {...form.getInputProps('passwordConfirm')}
              />
            </Grid.Col>
          </Grid>

          {form.errors.general && (
            <Text ta="center" c="red" size="sm" mt="xl">
              {form.errors.general}
            </Text>
          )}
          <Button
            type="submit"
            mx="auto"
            mt="xl"
            style={{ display: 'block' }}
            loading={isLoading}
          >
            {t('auth.passwordReset.submit')}
          </Button>
        </form>
      )}
    </Container>
  );
}
